/**
 * API functions for saving user interaction data to the DenoKV backend
 */

// Get the DenoKV API URL from environment variables or use a default
const DENOKV_API_URL = process.env.REACT_APP_DENOKV_API_URL || 'http://localhost:8000';

// Constants for backoff and storage management
const MAX_PENDING_INTERACTIONS = 1000; // Maximum number of pending interactions to store
const MAX_PENDING_BATCHES = 50; // Maximum number of batches to store
const MAX_PENDING_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const BACKOFF_INITIAL_MS = 5000; // Initial backoff time (5 seconds)
const BACKOFF_MAX_MS = 30 * 60 * 1000; // Maximum backoff time (30 minutes)
const AVAILABILITY_CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check server availability every 5 minutes

// State variables for backoff and availability
let lastServerAvailabilityCheck = 0;
let isServerAvailable = true;
let backoffUntil = 0;
let currentBackoffMs = BACKOFF_INITIAL_MS;

/**
 * Check if we should attempt to contact the server based on backoff and availability
 * @returns {boolean} - True if we should attempt to contact the server
 */
function shouldAttemptServerContact() {
  const now = Date.now();
  
  // If we're in backoff mode, don't attempt contact
  if (now < backoffUntil) {
    return false;
  }
  
  // If we know the server is unavailable and we haven't checked recently, don't attempt contact
  if (!isServerAvailable && (now - lastServerAvailabilityCheck) < AVAILABILITY_CHECK_INTERVAL_MS) {
    return false;
  }
  
  // If we're offline, don't attempt contact
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  
  // Otherwise, attempt contact
  return true;
}

/**
 * Update backoff state after a failed server contact
 */
function updateBackoffAfterFailure() {
  isServerAvailable = false;
  lastServerAvailabilityCheck = Date.now();
  
  // Increase backoff time exponentially, up to the maximum
  currentBackoffMs = Math.min(currentBackoffMs * 2, BACKOFF_MAX_MS);
  backoffUntil = Date.now() + currentBackoffMs;
  
  console.log(`Server unavailable, backing off for ${currentBackoffMs/1000} seconds until ${new Date(backoffUntil).toLocaleTimeString()}`);
}

/**
 * Reset backoff state after a successful server contact
 */
function resetBackoffAfterSuccess() {
  isServerAvailable = true;
  lastServerAvailabilityCheck = Date.now();
  currentBackoffMs = BACKOFF_INITIAL_MS;
  backoffUntil = 0;
}

/**
 * Clean up and manage pending interactions storage
 * @param {Array} pendingInteractions - The array of pending interactions
 * @returns {Array} - The cleaned up array of pending interactions
 */
function cleanupPendingInteractions(pendingInteractions) {
  if (!pendingInteractions || pendingInteractions.length === 0) {
    return [];
  }
  
  const now = Date.now();
  
  // Remove interactions that are too old
  const notTooOld = pendingInteractions.filter(batch => 
    (now - batch.timestamp) < MAX_PENDING_AGE_MS
  );
  
  // If we're still over the limit, keep only the most recent batches
  if (notTooOld.length > MAX_PENDING_BATCHES) {
    // Sort by timestamp (newest first)
    notTooOld.sort((a, b) => b.timestamp - a.timestamp);
    
    // Keep only the most recent batches
    return notTooOld.slice(0, MAX_PENDING_BATCHES);
  }
  
  return notTooOld;
}

/**
 * Count total interactions in pending batches
 * @param {Array} pendingInteractions - The array of pending interactions
 * @returns {number} - The total number of interactions
 */
function countTotalInteractions(pendingInteractions) {
  return pendingInteractions.reduce((total, batch) => 
    total + (batch.interactions ? batch.interactions.length : 0), 0);
}

/**
 * Save user interaction data to DenoKV
 * @param {Object} interactionData - The interaction data to save
 * @param {string} userId - The user ID
 * @param {string} sessionHash - The session hash
 * @returns {Promise<Object>} - Promise resolving to the saved interaction data
 */
export async function saveUserInteractionData(interactionData, userId, sessionHash) {
  // Get and clean up pending interactions
  let pendingInteractions = JSON.parse(localStorage.getItem('pendingInteractions') || '[]');
  pendingInteractions = cleanupPendingInteractions(pendingInteractions);
  
  // Add the new interactions to pending storage first (optimistic approach)
  pendingInteractions.push({
    userId,
    sessionHash,
    interactions: interactionData.interactions,
    timestamp: Date.now()
  });
  
  // Check if we have too many total interactions and trim if needed
  const totalInteractions = countTotalInteractions(pendingInteractions);
  if (totalInteractions > MAX_PENDING_INTERACTIONS) {
    console.warn(`Too many pending interactions (${totalInteractions}), trimming oldest batches`);
    
    // Sort by timestamp (oldest first)
    pendingInteractions.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove oldest batches until we're under the limit
    while (countTotalInteractions(pendingInteractions) > MAX_PENDING_INTERACTIONS && pendingInteractions.length > 1) {
      pendingInteractions.shift(); // Remove oldest batch
    }
  }
  
  // Save the updated pending interactions
  localStorage.setItem('pendingInteractions', JSON.stringify(pendingInteractions));
  
  // If we shouldn't attempt server contact, return early
  if (!shouldAttemptServerContact()) {
    return { success: false, offline: true, message: "Server contact skipped due to backoff or offline state" };
  }
  
  try {
    // Send the interaction data to the DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/userInteractions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        userId,
        sessionHash,
        interactions: interactionData.interactions
      }),
      credentials: 'omit',
      // Add a timeout to prevent long-hanging requests
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });

    if (!response.ok) {
      // If we get a 429 (Too Many Requests), back off for longer
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const retryMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : currentBackoffMs * 2;
        backoffUntil = Date.now() + retryMs;
        console.warn(`Rate limited by server, backing off for ${retryMs/1000} seconds`);
      } else {
        updateBackoffAfterFailure();
      }
      
      throw new Error(`Failed to save interactions: ${response.status} ${response.statusText}`);
    }
    
    // Success! Reset backoff and return the response
    resetBackoffAfterSuccess();
    return await response.json();
  } catch (error) {
    // Don't log connection refused errors at error level, they're too noisy
    if (error.message && error.message.includes('Failed to fetch')) {
      console.warn('Unable to connect to DenoKV server, will retry later');
    } else {
      console.error('Error saving user interaction data to DenoKV:', error);
    }
    
    // Update backoff state
    updateBackoffAfterFailure();
    
    // We've already stored the interactions in localStorage, so just return an error
    return { success: false, offline: true, message: "Stored for later sync" };
  }
}

/**
 * Save user session data to DenoKV
 * @param {Object} sessionData - The session data to save
 * @param {string} userId - The user ID
 * @param {string} sessionHash - The session hash
 * @returns {Promise<Object>} - Promise resolving to the saved session data
 */
export async function saveUserSessionData(sessionData, userId, sessionHash) {
  if (!userId || !sessionHash) {
    console.log('Session data would be saved to DenoKV:', sessionData);
    return { success: false, message: "Missing userId or sessionHash" };
  }
  
  // Store session data in localStorage first (optimistic approach)
  const pendingSessions = JSON.parse(localStorage.getItem('pendingSessions') || '[]');
  pendingSessions.push({
    userId,
    sessionHash,
    sessionData,
    timestamp: Date.now()
  });
  
  // Limit the number of pending sessions
  if (pendingSessions.length > 50) {
    pendingSessions.sort((a, b) => b.timestamp - a.timestamp);
    pendingSessions.length = 50; // Keep only the 50 most recent
  }
  
  localStorage.setItem('pendingSessions', JSON.stringify(pendingSessions));
  
  // If we shouldn't attempt server contact, return early
  if (!shouldAttemptServerContact()) {
    return { success: false, offline: true, message: "Server contact skipped due to backoff or offline state" };
  }
  
  try {
    // Send the session data to the DenoKV backend
    const response = await fetch(`${DENOKV_API_URL}/api/userSessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        userId,
        sessionHash,
        sessionData
      }),
      credentials: 'omit',
      // Add a timeout to prevent long-hanging requests
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });

    if (!response.ok) {
      // If we get a 429 (Too Many Requests), back off for longer
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const retryMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : currentBackoffMs * 2;
        backoffUntil = Date.now() + retryMs;
        console.warn(`Rate limited by server, backing off for ${retryMs/1000} seconds`);
      } else {
        updateBackoffAfterFailure();
      }
      
      throw new Error(`Failed to save session data: ${response.status} ${response.statusText}`);
    }
    
    // Success! Reset backoff and return the response
    resetBackoffAfterSuccess();
    return await response.json();
  } catch (error) {
    // Don't log connection refused errors at error level, they're too noisy
    if (error.message && error.message.includes('Failed to fetch')) {
      console.warn('Unable to connect to DenoKV server, will retry later');
    } else {
      console.error('Error saving user session data to DenoKV:', error);
    }
    
    // Update backoff state
    updateBackoffAfterFailure();
    
    // We've already stored the session data in localStorage, so just return an error
    return { success: false, offline: true, message: "Stored for later sync" };
  }
}

/**
 * Sync pending interactions when online
 * @returns {Promise<number>} - Promise resolving to the number of synced interactions
 */
export async function syncPendingInteractions() {
  // If we shouldn't attempt server contact, return early
  if (!shouldAttemptServerContact()) {
    return 0;
  }
  
  try {
    // Check if the server is available first
    const available = await isDenoKVBackendAvailable();
    if (!available) {
      updateBackoffAfterFailure();
      return 0;
    }
    
    // Get and clean up pending interactions
    let pendingInteractions = JSON.parse(localStorage.getItem('pendingInteractions') || '[]');
    if (pendingInteractions.length === 0) {
      return 0;
    }
    
    pendingInteractions = cleanupPendingInteractions(pendingInteractions);
    
    // If we have too many batches, only sync the most recent ones
    if (pendingInteractions.length > 10) {
      pendingInteractions.sort((a, b) => b.timestamp - a.timestamp);
      pendingInteractions = pendingInteractions.slice(0, 10);
    }

    let syncedCount = 0;
    let remainingInteractions = [];
    
    // Process each pending interaction batch
    for (const batch of pendingInteractions) {
      try {
        const result = await saveUserInteractionData(
          { interactions: batch.interactions },
          batch.userId,
          batch.sessionHash
        );
        
        if (result.success !== false) {
          syncedCount += batch.interactions.length;
        } else {
          // If we failed to sync this batch, keep it for later
          remainingInteractions.push(batch);
        }
      } catch (error) {
        // If we get an error, keep this batch for later
        remainingInteractions.push(batch);
        
        // If we've had multiple failures, stop trying for now
        if (remainingInteractions.length >= 3) {
          console.warn('Multiple sync failures, stopping sync attempt');
          break;
        }
      }
    }

    // Update the pending interactions in localStorage
    if (remainingInteractions.length > 0) {
      localStorage.setItem('pendingInteractions', JSON.stringify(remainingInteractions));
    } else {
      localStorage.removeItem('pendingInteractions');
    }
    
    // If we synced any interactions, reset the backoff
    if (syncedCount > 0) {
      resetBackoffAfterSuccess();
    }
    
    return syncedCount;
  } catch (error) {
    console.error('Error syncing pending interactions:', error);
    updateBackoffAfterFailure();
    return 0;
  }
}

/**
 * Sync pending sessions when online
 * @returns {Promise<number>} - Promise resolving to the number of synced sessions
 */
export async function syncPendingSessions() {
  // If we shouldn't attempt server contact, return early
  if (!shouldAttemptServerContact()) {
    return 0;
  }
  
  try {
    // Check if the server is available first
    const available = await isDenoKVBackendAvailable();
    if (!available) {
      updateBackoffAfterFailure();
      return 0;
    }
    
    const pendingSessions = JSON.parse(localStorage.getItem('pendingSessions') || '[]');
    if (pendingSessions.length === 0) {
      return 0;
    }

    let syncedCount = 0;
    let remainingSessions = [];
    
    // Process each pending session
    for (const session of pendingSessions) {
      try {
        const result = await saveUserSessionData(
          session.sessionData,
          session.userId,
          session.sessionHash
        );
        
        if (result.success !== false) {
          syncedCount++;
        } else {
          // If we failed to sync this session, keep it for later
          remainingSessions.push(session);
        }
      } catch (error) {
        // If we get an error, keep this session for later
        remainingSessions.push(session);
        
        // If we've had multiple failures, stop trying for now
        if (remainingSessions.length >= 3) {
          console.warn('Multiple sync failures, stopping sync attempt');
          break;
        }
      }
    }

    // Update the pending sessions in localStorage
    if (remainingSessions.length > 0) {
      localStorage.setItem('pendingSessions', JSON.stringify(remainingSessions));
    } else {
      localStorage.removeItem('pendingSessions');
    }
    
    // If we synced any sessions, reset the backoff
    if (syncedCount > 0) {
      resetBackoffAfterSuccess();
    }
    
    return syncedCount;
  } catch (error) {
    console.error('Error syncing pending sessions:', error);
    updateBackoffAfterFailure();
    return 0;
  }
}

/**
 * Check if the DenoKV backend is available
 * @returns {Promise<boolean>} - Promise resolving to true if the backend is available
 */
export async function isDenoKVBackendAvailable() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(`${DENOKV_API_URL}/api/health`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    const available = data.status === 'ok' && data.kvConnected === true;
    
    // Update our availability state
    if (available) {
      resetBackoffAfterSuccess();
    } else {
      updateBackoffAfterFailure();
    }
    
    return available;
  } catch (error) {
    // Don't log connection refused errors at error level, they're too noisy
    if (error.name === 'AbortError') {
      console.warn('Health check timed out');
    } else if (error.message && error.message.includes('Failed to fetch')) {
      console.warn('Unable to connect to DenoKV server');
    } else {
      console.error('Error checking DenoKV backend availability:', error);
    }
    
    updateBackoffAfterFailure();
    return false;
  }
}

/**
 * Get the count of pending interactions
 * @returns {number} - The number of pending interactions
 */
export function getPendingInteractionsCount() {
  try {
    const pendingInteractions = JSON.parse(localStorage.getItem('pendingInteractions') || '[]');
    return countTotalInteractions(pendingInteractions);
  } catch (error) {
    console.error('Error getting pending interactions count:', error);
    return 0;
  }
}

/**
 * Get the count of pending sessions
 * @returns {number} - The number of pending sessions
 */
export function getPendingSessionsCount() {
  try {
    const pendingSessions = JSON.parse(localStorage.getItem('pendingSessions') || '[]');
    return pendingSessions.length;
  } catch (error) {
    console.error('Error getting pending sessions count:', error);
    return 0;
  }
}

/**
 * Clear all pending interactions and sessions
 */
export function clearPendingData() {
  localStorage.removeItem('pendingInteractions');
  localStorage.removeItem('pendingSessions');
  console.log('Cleared all pending interaction and session data');
}
